import React, { useState, useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList, ResponsiveContainer,
} from 'recharts';
import { AnalyticsPeriod, PeriodKey } from '../../../../hooks/useBoardAnalytics';
import DateRangePills from './DateRangePills';

interface ActivitiesByTypeChartProps {
  periods: Record<PeriodKey, AnalyticsPeriod>;
  showValues?: boolean;
}

const LINE_COLORS = ['#111827', '#4b5563', '#6b7280', '#9ca3af', '#374151', '#1f2937', '#d1d5db'];

function formatPeriodLabel(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s: number, p: any) => s + (p.value || 0), 0);
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px', fontSize: 12, minWidth: 140 }}>
      <div style={{ color: '#6b7280', marginBottom: 6 }}>{formatPeriodLabel(label)}</div>
      {payload.map((p: any) => (
        <div key={p.dataKey} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 2 }}>
          <span style={{ color: p.color, fontWeight: 500 }}>{p.dataKey}</span>
          <span style={{ color: '#111827', fontWeight: 600 }}>
            {p.value} <span style={{ color: '#9ca3af', fontWeight: 400 }}>({total > 0 ? Math.round(p.value / total * 100) : 0}%)</span>
          </span>
        </div>
      ))}
    </div>
  );
}

export default function ActivitiesByTypeChart({ periods, showValues = true }: ActivitiesByTypeChartProps) {
  const [period, setPeriod] = useState<PeriodKey>('30d');
  const raw = periods[period]?.activities_by_type ?? [];

  const { chartData, types } = useMemo(() => {
    const periodMap: Record<string, Record<string, number>> = {};
    const typeSet = new Set<string>();

    raw.forEach(({ type, period: p, count }) => {
      if (!p) return;
      if (!periodMap[p]) periodMap[p] = {};
      periodMap[p][type] = (periodMap[p][type] || 0) + count;
      typeSet.add(type);
    });

    const sortedPeriods = Object.keys(periodMap).sort();
    const data = sortedPeriods.map((p) => ({ period: p, ...periodMap[p] }));
    return { chartData: data, types: Array.from(typeSet) };
  }, [raw]);

  // Label every other point (plus the last) so the line reads as a trend, not
  // just an endpoint — while skipping zeros/odd points to avoid clutter.
  const lastIdx = chartData.length - 1;
  const renderValueLabel = (props: any) => {
    const { x, y, index, value } = props;
    if (!value) return null;
    const show = index % 2 === 0 || index === lastIdx;
    if (!show) return null;
    return (
      <text x={x} y={y - 6} fontSize={10} fontWeight={600} fill="#6b7280" textAnchor="middle">
        {value}
      </text>
    );
  };

  return (
    <div className="metrics-card">
      <div className="metrics-card__header">
        <span className="metrics-card__title">Activities by Type</span>
        <DateRangePills value={period} onChange={setPeriod} />
      </div>
      {chartData.length === 0 ? (
        <div className="metrics-empty">No activities yet</div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={chartData} margin={{ top: 0, right: showValues ? 28 : 8, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
            <XAxis
              dataKey="period"
              tickFormatter={formatPeriodLabel}
              tick={{ fontSize: 11, fill: '#9ca3af' }}
              axisLine={false}
              tickLine={false}
              interval="preserveStartEnd"
            />
            <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
            {types.map((type, idx) => (
              <Line
                key={type}
                type="monotone"
                dataKey={type}
                stroke={LINE_COLORS[idx % LINE_COLORS.length]}
                strokeWidth={2}
                dot={showValues ? { r: 2 } : false}
                activeDot={{ r: 4 }}
              >
                {showValues && <LabelList content={renderValueLabel} />}
              </Line>
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
